'use client';

import React from 'react';
import { AppProviders, type AppProvidersProps } from './AppProviders';
import { ThemeProvider } from '../components/ThemeProvider';
import { ErrorBoundary } from '../components/ErrorBoundary';

interface ClientProvidersProps extends Omit<AppProvidersProps, 'queryClient'> {
  children: React.ReactNode;
  /** Theme configuration */
  defaultTheme?: 'light' | 'dark' | 'system';
  /** Storage key for theme persistence (defaults to 'bite-theme') */
  themeStorageKey?: string;
  /** Whether to enable system theme detection */
  enableSystem?: boolean;
  /** Custom error boundary fallback */
  errorFallback?: React.ReactNode;
  /** Error reporting callback */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

export function ClientProviders({
  defaultTheme = 'system',
  themeStorageKey = 'bite-theme',
  enableSystem = true,
  errorFallback,
  onError,
  children,
  ...appProviderProps
}: ClientProvidersProps) {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error('[ClientProviders] Unhandled error:', error, errorInfo);
    onError?.(error, errorInfo);
  };

  return (
    <ErrorBoundary
      fallback={errorFallback}
      onError={handleError}
      resetKeys={[themeStorageKey, defaultTheme]}
    >
      <ThemeProvider
        defaultTheme={defaultTheme}
        storageKey={themeStorageKey}
        enableSystem={enableSystem}
      >
        <AppProviders {...appProviderProps}>
          {children}
        </AppProviders>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
